/**
 * Patient Edit Page Hook
 *
 * Custom hook that encapsulates all business logic for the Patient Edit page
 * Uses centralized React Query hooks from @hooks/main/useMainPatientEdit
 * Separates concerns: page component only handles rendering
 *
 * Implements all RA2 functionality including:
 * - Form validation rules (age, insurance type, dates)
 * - Character normalization (full-width/half-width conversion)
 * - Auto-sync patient info to insurance card
 * - Insurance type auto-defaults
 * - Reimbursement (償還払い) detection
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
  type BaseSyntheticEvent,
} from 'react';
import { useParams, useNavigate, useLocation, type SetURLSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';

import {
  PatientFormSchemaType,
  patientFormSchema,
  PatientFormData,
  defaultPatientFormData,
} from '@pages/main/patient/types';
import { usePatientMasterData } from '@/hooks/main/usePatientMasterData';
import { useMainPatientEdit, useMainPatientEditMutation } from '@hooks/main';
import { useAuth } from '@shared/providers/AuthContext';
import { usePatientFieldNormalization } from '@/hooks/usePatientFieldNormalization';
import { useInsuranceTypeLogic, useMedicalAidTypeLogic } from '@/hooks/useInsuranceTypeLogic';
import { useSyncPatientToInsurance, useNameFieldSync } from '@/hooks/useSyncPatientToInsurance';
import { calculateAge } from '@/utils/patientValidation';
import { useCrossSectionValidation } from './useCrossSectionValidation';
import { useGlobalBehavior } from '@/shared/providers/GlobalBehaviorWrapper';
import { isInsuredSelfRelationship } from '@pages/main/patient/utils/patientHistoryHelpers';
import {
  applyLegacyPatientEditSearchParams,
  hasPatientEditHistorySearchParams,
  hasStaleOriginalCurrentIds,
  PATIENT_EDIT_HISTORY_SEARCH,
} from '@pages/main/patient/utils/patientEditHistoryUrl';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import {
  clearLatestEditingPatientId,
  setLatestEditingPatientId,
} from '@shared/lib/latestEditingPatient';
import useAppModal from '@/hooks/useAppModal';
import useAppToast from '@/hooks/useAppToast';
import {
  fieldErrorsTreeEmpty,
  getDefaultFormValues,
  mergeCertificationCopiedPatient,
  normalizePatientData,
  patientFormDataForNewRegistrationCopy,
  toFormValues,
} from './usePatientEditPage.formValues';
import { usePatientEditPageSearchHandlers } from './usePatientEditPage.searchHandlers';
import { usePatientEditPageHistoryWorkflow } from './usePatientEditPage.historyWorkflow';
import { usePatientEditPageSaveActions } from './usePatientEditPage.saveActions';
import type {
  PatientSubmittingAction,
  UsePatientEditPageOptions,
  UsePatientEditPageReturn,
} from './usePatientEditPage.types';

export type {
  PatientEditHistoryUrlSync,
  PatientSubmittingAction,
  UsePatientEditPageOptions,
  UsePatientEditPageReturn,
} from './usePatientEditPage.types';

// ==================== Hook ====================

export const usePatientEditPage = (
  options: UsePatientEditPageOptions = {}
): UsePatientEditPageReturn => {
  /** A2-style URL sync (PatientEditPage passes useSearchParams). Refs avoid stale closures and unstable hook deps. */
  /**
   * Tracks the location.state.copiedPatient reference that has already been merged.
   * BD 6.1 — Prevents re-merging stale cert data on query refetch after save,
   * but still allows merging when user navigates with a NEW cert payload.
   */
  const certMergedPatientRef = useRef<PatientFormData | null>(null);
  /**
   * Tracks whether the 履歴作成 mode flag has already been applied for the current cert nav.
   * Prevents re-enabling history mode after a successful save (which would cause a 2nd
   * history record on next save).
   */
  const certHistoryAppliedRef = useRef(false);
  const historySearchParamsRef = useRef<URLSearchParams | undefined>(undefined);
  const setHistorySearchParamsRef = useRef<SetURLSearchParams | undefined>(undefined);
  const onInsurerSelectedNonKoreiRef = useRef<(() => void) | undefined>(undefined);
  const historySearchParamsKey = options.historyUrl?.searchParams?.toString() ?? '';
  useLayoutEffect(() => {
    const hu = options.historyUrl;
    historySearchParamsRef.current = hu?.searchParams;
    setHistorySearchParamsRef.current = hu?.setSearchParams;
    onInsurerSelectedNonKoreiRef.current = options.onInsurerSelectedNonKorei;
  });

  const { id: routeId, tenant, copyFromId } = useParams<{
    id?: string;
    tenant?: string;
    copyFromId?: string;
  }>();

  const navigate = useNavigate();
  const location = useLocation();
  const modal = useAppModal();
  const { serviceType: contextServiceType } = useTheme();
  const toast = useAppToast();
  const { clinic } = useAuth();

  const queryClient = useQueryClient();
  /** An embedder may supply the patient instead of the URL. */
  const id = options.patientId ?? routeId;

  const isEditMode = Boolean(id);
  const isCopyMode = Boolean(copyFromId);

  const patientQueryNumericId = useMemo(() => {
    if (id) {
      const n = parseInt(id, 10);
      return Number.isFinite(n) ? n : undefined;
    }
    if (copyFromId) {
      const n = parseInt(copyFromId, 10);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  }, [id, copyFromId]);

  // Use centralized React Query hooks (automatically handle clinic context)
  const {
    data: patientQueryData,
    isLoading: isLoadingPatient,
    isError: isPatientQueryError,
    refetch: refetchPatient,
  } = useMainPatientEdit(patientQueryNumericId);
  const mutations = useMainPatientEditMutation();
  usePatientMasterData(); // Initialize master data

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingAction, setSubmittingAction] = useState<PatientSubmittingAction | null>(null);
  const [patientData, setPatientData] = useState<PatientFormData | null>(null);
  const { clearFormChange } = useGlobalBehavior();
  const [initialFormValuesForGuard, setInitialFormValuesForGuard] =
    useState<PatientFormSchemaType | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [includeExcluded, setIncludeExcluded] = useState(false);

  /**
   * Tracks the original "true current" record IDs from the backend's initial response.
   * These are captured on first load (without history URL params) and preserved
   * across history switches so the UI can show 「（履歴表示）」 when viewing a non-current record.
   */
  const originalCurrentIdsRef = useRef<{
    patientId: number | null;
    patientInformationId: number | undefined;
    healthInsuranceCardId: number | undefined;
    medicalAidId: number | undefined;
  }>({
    patientId: null,
    patientInformationId: undefined,
    healthInsuranceCardId: undefined,
    medicalAidId: undefined,
  });

  /** Update originalCurrentIdsRef from normalized patient data (call after successful save/submit). */
  const syncOriginalCurrentIds = useCallback((data: PatientFormData) => {
    originalCurrentIdsRef.current = {
      patientId: data.id,
      patientInformationId: data.current_patient_information?.id,
      healthInsuranceCardId: data.health_insurance_cards?.find(c => c.is_current)?.id,
      medicalAidId: data.medical_aids?.find(a => a.is_current)?.id,
    };
    // Sync original medical aid insurer_type_id for A2 Step 12
    const currentAid = data.medical_aids?.find(a => a.is_current) || data.medical_aids?.[0];
    const rawTypeId = currentAid?.insurer_type_id ?? null;
    originalMedicalAidInsurerTypeIdRef.current = rawTypeId === 1 ? 0 : rawTypeId;
  }, []);

  // React Hook Form — Zod の field エラーは wrapHandleSubmit（登録）経由のときだけ返す
  const patientPostalSubmitValidationRef = useRef(false);
  const patientFormResolver = useMemo((): Resolver<PatientFormSchemaType> => {
    const base = zodResolver(patientFormSchema);
    return async (values, context, options) => {
      const result = await base(values, context, options);
      // wrapHandleSubmit sets patientPostalSubmitValidationRef true only for 登録 submit.
      // RHF may still invoke the resolver outside that path; suppress all Zod errors until then
      // so required-field messages (氏名漢字/カナ等) do not show before submit.
      if (
        !patientPostalSubmitValidationRef.current &&
        result.errors &&
        !fieldErrorsTreeEmpty(result.errors)
      ) {
        return {
          values: values as PatientFormSchemaType,
          errors: {} as Record<string, never>,
        };
      }
      return result;
    };
  }, [modal]);

  const form = useTrimmedForm<PatientFormSchemaType>({
    resolver: patientFormResolver,
    defaultValues: getDefaultFormValues(),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const wrapHandleSubmit = useCallback(
    ((...args) => {
      const [onValid, onInvalid] = args;
      return (e?: BaseSyntheticEvent) => {
        patientPostalSubmitValidationRef.current = true;
        return form.handleSubmit(
          async data => {
            patientPostalSubmitValidationRef.current = false;
            await onValid(data);
          },
          errs => {
            patientPostalSubmitValidationRef.current = false;
            onInvalid?.(errs);
          }
        )(e);
      };
    }) as typeof form.handleSubmit,
    [form]
  );

  const { watch, setValue, reset, getValues, setFocus } = form;

  // ==================== RA2 UI/UX Feature Hooks ====================

  // Insurance type logic (field states, auto-defaults, reimbursement)
  const {
    state: insuranceTypeState,
    handleInsuranceTypeChange,
    handleInsurerNumberChange,
  } = useInsuranceTypeLogic(watch, setValue);

  // Medical aid type logic
  const { isFieldsDisabled: isMedicalAidFieldsDisabled, handleMedicalAidTypeChange } =
    useMedicalAidTypeLogic(watch, setValue);

  // Patient-to-insurance sync (disabled - keep them separate)
  useSyncPatientToInsurance(watch, setValue, { enabled: false });

  // Original medical aid insurer_type_id for A2 Step 12 (type-changed check).
  // HIT-14779: re-captured every time the form is (re)loaded — switching patient on the same
  // screen, history switch, blank / copy form — from the 種類 actually shown in the form.
  // It used to be captured only once per screen, so after moving from a patient with another
  // 種類 an untouched 登録 failed with 「入力されたコードの種別が違います。」. Also updated after save.
  const originalMedicalAidInsurerTypeIdRef = useRef<number | null | undefined>(undefined);
  const captureMedicalAidTypeBaseline = (values: {
    medical_aid?: { insurer_type_id?: number | null };
  }) => {
    originalMedicalAidInsurerTypeIdRef.current = values.medical_aid?.insurer_type_id ?? null;
  };

  const {
    isPatientInfoValidityModalOpen,
    isBulkHistoryDateModalOpen,
    isNewHistoryMode,
    singleHistoryAvailableToMinus,
    singleHistoryQualificationDateYmd,
    setIsNewHistoryMode,
    setSingleHistoryAvailableToMinus,
    setSingleHistoryQualificationDateYmd,
    handleHistorySwitch,
    handleHistoryDelete,
    handleBulkCreateHistory,
    handleBulkHistoryDateConfirm,
    handleBulkHistoryDateModalClose,
    handlePatientInfoValidityModalClose,
    handlePatientInfoValidityModalConfirm,
    handlePatientHistoryCreateCancel,
  } = usePatientEditPageHistoryWorkflow({
    patientData,
    patientQueryNumericId,
    refetchPatient,
    clinic,
    getValues,
    reset,
    setValue,
    setPatientData,
    setInitialFormValuesForGuard,
    syncOriginalCurrentIds,
    setHistorySearchParamsRef,
    historySearchParamsRef,
    deleteHistory: mutations.deleteHistory,
    modal,
    toast,
  });

  const {
    isPatientSearchModalOpen,
    isAddressSearchModalOpen,
    isInsuranceAddressSearchModalOpen,
    isInsurerSearchModalOpen,
    pendingInsurerResults,
    pendingInsurerTotalCount,
    isKoreiModalOpen,
    pendingKoreiInsurer,
    isMedicalAidSearchModalOpen,
    pendingMedicalAidIntervalRows,
    isMedicalAidDirectSearchLoading,
    handlePatientSearch,
    handlePatientSearchClose,
    handlePatientSearchSelect,
    handleAddressSearch,
    handleAddressSearchClose,
    handleAddressSearchSelect,
    handleOpenMap,
    handleInsuranceAddressSearch,
    handleInsuranceAddressSearchClose,
    handleInsuranceAddressSearchSelect,
    handleInsuranceOpenMap,
    handleInsurerSearch,
    isInsurerSearchLoading,
    waitForPendingInsurerSearch,
    handleInsurerSearchClose,
    handleInsurerSearchSelect,
    handleKoreiModalClose,
    handleKoreiModalSelect,
    handleMedicalAidSearch,
    handleMedicalAidSearchClose,
    handleMedicalAidSearchSelect,
    handleMedicalAidNumberDirectSearch,
  } = usePatientEditPageSearchHandlers({
    searchKeyword,
    includeExcluded,
    setSearchKeyword,
    tenant,
    navigate,
    modal,
    toast,
    clinic,
    watch,
    setValue,
    getValues,
    setFocus,
    originalMedicalAidInsurerTypeIdRef,
    onInsurerSelectedNonKoreiRef,
  });

  // Cross-section validation (A2 steps 1-15 in exact order)
  const { runAllValidations } = useCrossSectionValidation({
    watch,
    originalMedicalAidInsurerTypeId: originalMedicalAidInsurerTypeIdRef.current,
  });

  // Name field sync with family name extraction
  const { handleNameChange, handleNameKanaChange } = useNameFieldSync(watch, setValue);

  // Field normalization handlers
  const fieldNormalization = usePatientFieldNormalization(watch, setValue);

  // Calculate patient age
  const patientDateOfBirth = watch('patient_information.date_of_birth');
  const patientAge = useMemo(() => {
    if (!patientDateOfBirth) return null;
    return calculateAge(patientDateOfBirth);
  }, [patientDateOfBirth]);

  // Insured fields are readonly when 続柄 is 本人 (id 1) only — not 67（本人）; same on new/copy/edit
  const relationshipId = watch('health_insurance_card.relationship_id');
  const isInsuredFieldsReadonly = isInsuredSelfRelationship(relationshipId);

  // Handler for insurer number blur (combines normalization + type detection)
  const handleInsurerNumberBlur = useCallback(() => {
    fieldNormalization.normalizeInsurerNumber();
    const insurerNumber = watch('health_insurance_card.insurer_number');
    if (insurerNumber) {
      handleInsurerNumberChange(insurerNumber);
    }
  }, [fieldNormalization, watch, handleInsurerNumberChange]);

  useEffect(() => {
    if (!isEditMode || !id) {
      return;
    }

    setLatestEditingPatientId(tenant, id, clinic?.clinic_number);
  }, [isEditMode, id, tenant, clinic?.clinic_number]);

  useEffect(() => {
    if (!isEditMode || !isPatientQueryError) {
      return;
    }

    clearLatestEditingPatientId(tenant, clinic?.clinic_number);
  }, [isEditMode, isPatientQueryError, tenant, clinic?.clinic_number]);

  // Load patient data from query (edit / copy-from) or blank / certification state (create)
  useEffect(() => {
    if (isEditMode) {
      if (patientQueryData) {
        let normalizedData = normalizePatientData(patientQueryData);
        const sp = historySearchParamsRef.current;
        const hasHistoryParams = sp && hasPatientEditHistorySearchParams(sp);

        // BD 6.1 — When arriving from /patients/certification (受診者上書登録 / 履歴作成),
        // autofill cert form data on top of DB record. Address fields (postal_code1/2,
        // address1, address2) are kept from DB per BD.
        // Apply only ONCE per navigation — refetch after save must not re-merge stale cert data.
        const certNavState = location.state as
          | { copiedPatient?: PatientFormData; isHistoryCreate?: boolean }
          | undefined;
        const certData = certNavState?.copiedPatient;
        const isHistoryCreateFromCert = !!certNavState?.isHistoryCreate;
        if (certData && certMergedPatientRef.current !== certData) {
          certMergedPatientRef.current = certData;
          // Reset history applied flag for new cert navigation
          certHistoryAppliedRef.current = false;
          normalizedData = mergeCertificationCopiedPatient(normalizedData, certData);
        }

        // Capture original current IDs on first load or when no history URL params
        // (e.g. after save, clean page load). Don't update during history-switch refetches
        // so the UI can compare against these to show 「（履歴表示）」.
        // Deleting the current row keeps the other sections' params, so also re-capture when
        // a remembered id is gone; otherwise the promoted row stays grey as （履歴表示）.
        if (
          normalizedData.id !== originalCurrentIdsRef.current.patientId ||
          !hasHistoryParams ||
          hasStaleOriginalCurrentIds(originalCurrentIdsRef.current, normalizedData)
        ) {
          originalCurrentIdsRef.current = {
            patientId: normalizedData.id,
            patientInformationId: normalizedData.current_patient_information?.id,
            healthInsuranceCardId: normalizedData.health_insurance_cards?.find(c => c.is_current)
              ?.id,
            medicalAidId: normalizedData.medical_aids?.find(a => a.is_current)?.id,
          };
        }

        if (hasHistoryParams) {
          normalizedData = normalizePatientData(
            applyLegacyPatientEditSearchParams(normalizedData, sp)
          );
        }
        const formValues = toFormValues(normalizedData);
        const bulkQualificationDate =
          hasHistoryParams && sp.get(PATIENT_EDIT_HISTORY_SEARCH.type) === 'all_change'
            ? sp.get(PATIENT_EDIT_HISTORY_SEARCH.bulk_qualification_date)?.trim().slice(0, 10)
            : undefined;
        if (bulkQualificationDate) {
          const d = new Date(`${bulkQualificationDate}T12:00:00`);
          formValues._bulkHistoryQualificationDate = bulkQualificationDate;
          if (!Number.isNaN(d.getTime())) {
            d.setDate(d.getDate() - 1);
            formValues._bulkHistoryAvailableToMinus = d.toISOString().split('T')[0];
          }
        }
        setPatientData(normalizedData);
        setInitialFormValuesForGuard(formValues);
        reset(formValues, { keepDirty: false, keepTouched: false });
        captureMedicalAidTypeBaseline(formValues);
        if (hasHistoryParams) {
          const bulkByUrl =
            formValues.patient_information.id === undefined &&
            formValues.health_insurance_card.id === undefined;
          setIsNewHistoryMode(Boolean(bulkByUrl && sp.get('type') === 'all_change'));
        } else if (
          certData &&
          isHistoryCreateFromCert &&
          certMergedPatientRef.current === certData
        ) {
          // BD 6.1 — 受診者履歴作成 button → bulkCreateHistory mutation on save.
          // Only enable on the first apply of this cert nav state. After save+refetch,
          // ref stays equal but we should not re-enable; refer to certHistoryAppliedRef.
          if (!certHistoryAppliedRef.current) {
            certHistoryAppliedRef.current = true;
            setIsNewHistoryMode(true);
          } else {
            setIsNewHistoryMode(false);
          }
        } else {
          setIsNewHistoryMode(false);
        }
        setIsLoading(false);
      } else if (isPatientQueryError) {
        setPatientData(null);
        setIsLoading(false);
      } else if (isLoadingPatient) {
        setIsLoading(true);
      }
      return;
    }

    if (isCopyMode) {
      if (patientQueryData) {
        const normalizedData = normalizePatientData(patientQueryData);
        const copyFormData = patientFormDataForNewRegistrationCopy(normalizedData);
        const formValues = toFormValues(copyFormData);

        setPatientData(copyFormData);
        setInitialFormValuesForGuard(formValues);
        reset(formValues, { keepDirty: false, keepTouched: false });
        captureMedicalAidTypeBaseline(formValues);
        setIsNewHistoryMode(false);
        setIsLoading(false);
      } else if (isPatientQueryError) {
        setPatientData(null);
        setIsLoading(false);
      } else if (isLoadingPatient) {
        setIsLoading(true);
      }
      return;
    }

    // Plain create: certification copy via location.state (keep 受診者氏名・生年月日), or empty form.
    // BD 6.1 — Plain create flow is for NEW patient only (受診者新規登録 / no-duplicate copy).
    // 履歴作成 always navigates to /patients/edit/{id} (isEditMode=true), handled above.
    const navState = location.state as { copiedPatient?: PatientFormData } | undefined;
    const copiedPatient = navState?.copiedPatient;

    if (copiedPatient) {
      const normalizedData = normalizePatientData(copiedPatient);
      const formValues = toFormValues(normalizedData);
      setPatientData(normalizedData);
      setInitialFormValuesForGuard(formValues);
      reset(formValues, { keepDirty: false, keepTouched: false });
      captureMedicalAidTypeBaseline(formValues);
      setIsNewHistoryMode(false);
      setIsLoading(false);
    } else {
      const defaultValues = getDefaultFormValues();
      setPatientData(defaultPatientFormData);
      setInitialFormValuesForGuard(defaultValues);
      reset(defaultValues, { keepDirty: false, keepTouched: false });
      captureMedicalAidTypeBaseline(defaultValues);
      setIsNewHistoryMode(false);
      setIsLoading(false);
    }
  }, [
    patientQueryData,
    isLoadingPatient,
    isPatientQueryError,
    isEditMode,
    isCopyMode,
    reset,
    location.state,
    historySearchParamsKey,
  ]);

  const hasLoadError = (isEditMode || isCopyMode) && isPatientQueryError && !patientQueryData;

  const { onSubmit, handleProvisionalSave, handleDelete } = usePatientEditPageSaveActions({
    form,
    mutations,
    queryClient,
    navigate,
    modal,
    toast,
    clinic,
    tenant,
    id,
    isEditMode,
    isNewHistoryMode,
    patientData,
    initialFormValuesForGuard,
    singleHistoryAvailableToMinus,
    singleHistoryQualificationDateYmd,
    historySearchParamsRef,
    setHistorySearchParamsRef,
    setPatientData,
    setInitialFormValuesForGuard,
    setIsSubmitting,
    setSubmittingAction,
    setIsNewHistoryMode,
    setSingleHistoryAvailableToMinus,
    setSingleHistoryQualificationDateYmd,
    syncOriginalCurrentIds,
    clearFormChange,
    runAllValidations,
  });

  // Compute medical aid expiration status
  const medicalAidExpirationDate = watch('medical_aid.application_end_date');
  const isMedicalAidExpired = useMemo(() => {
    if (!medicalAidExpirationDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expDate = new Date(medicalAidExpirationDate);
    return expDate < today;
  }, [medicalAidExpirationDate]);

  const handleClearForm = useCallback(() => {
    // Reset form to default values
    const defaultValues = getDefaultFormValues();
    setInitialFormValuesForGuard(defaultValues);
    reset(defaultValues, { keepDirty: false, keepTouched: false });
    captureMedicalAidTypeBaseline(defaultValues);
    setPatientData(defaultPatientFormData);
    clearFormChange();

    // Clear search keyword input
    setSearchKeyword('');
    // Clear any stored "latest editing patient" id so UI won't reopen that patient
    clearLatestEditingPatientId(tenant, clinic?.clinic_number);
    navigate(`/${tenant}/patients/edit`);
  }, [
    contextServiceType,
    setSearchKeyword,
    reset,
    clearLatestEditingPatientId,
    navigate,
    tenant,
    clinic?.clinic_number,
  ]);

  return {
    // State
    isEditMode,
    isLoading,
    hasLoadError,
    isSubmitting,
    submittingAction,
    patientData,
    searchKeyword,
    includeExcluded,
    isPatientSearchModalOpen,
    // Modal states
    isAddressSearchModalOpen,
    isInsuranceAddressSearchModalOpen,
    isInsurerSearchModalOpen,
    pendingInsurerResults,
    pendingInsurerTotalCount,
    isKoreiModalOpen,
    pendingKoreiInsurer,
    isMedicalAidSearchModalOpen,
    pendingMedicalAidIntervalRows,
    isMedicalAidDirectSearchLoading,
    // Patient info validity date modal
    isPatientInfoValidityModalOpen,
    // Bulk history date modal
    isBulkHistoryDateModalOpen,
    // Medical aid expiration
    isMedicalAidExpired,
    // New history creation mode
    isNewHistoryMode,

    // Original current record IDs (for 「（履歴表示）」 title detection)
    originalCurrentIds: {
      patientInformationId: originalCurrentIdsRef.current.patientInformationId,
      healthInsuranceCardId: originalCurrentIdsRef.current.healthInsuranceCardId,
      medicalAidId: originalCurrentIdsRef.current.medicalAidId,
    },

    // Form
    form: { ...form, handleSubmit: wrapHandleSubmit },
    initialFormValuesForGuard,

    // State setters
    setSearchKeyword,
    setIncludeExcluded,
    // ==================== RA2 UI/UX Feature States ====================
    insuranceTypeState,
    isInsuredFieldsReadonly,
    isMedicalAidFieldsDisabled,
    patientAge,

    // Handlers
    onSubmit,
    handleProvisionalSave,
    handleDelete,
    handleHistorySwitch,
    handleHistoryDelete,
    handleBulkCreateHistory,
    handleBulkHistoryDateConfirm,
    handleBulkHistoryDateModalClose,
    handlePatientSearch,
    handlePatientSearchClose,
    handlePatientSearchSelect,
    handleAddressSearch,
    handleAddressSearchClose,
    handleAddressSearchSelect,
    handleOpenMap,
    handleInsuranceAddressSearch,
    handleInsuranceAddressSearchClose,
    handleInsuranceAddressSearchSelect,
    handleInsuranceOpenMap,
    handleInsurerSearch,
    isInsurerSearchLoading,
    waitForPendingInsurerSearch,
    handleInsurerSearchClose,
    handleInsurerSearchSelect,
    handleKoreiModalClose,
    handleKoreiModalSelect,
    handleMedicalAidSearch,
    handleMedicalAidSearchClose,
    handleMedicalAidSearchSelect,
    handleMedicalAidNumberDirectSearch,
    // Patient info validity date modal handlers
    handlePatientInfoValidityModalClose,
    handlePatientInfoValidityModalConfirm,
    handlePatientHistoryCreateCancel,
    handleClearForm,

    // ==================== RA2 UI/UX Feature Handlers ====================
    handleInsuranceTypeChange,
    handleInsurerNumberBlur,
    handleMedicalAidTypeChange,
    fieldNormalization,
    handleNameChange,
    handleNameKanaChange,

    // Navigation
    navigate,
    id,
    tenant,
    setFocus,
  };
};

export default usePatientEditPage;
